<p-panel [header]="'Fabric_Summary_Table_WeightUnit' | translateWF: 'Weight Unit:'">
  <!-- UOM Selector -->
  <ng-template pTemplate="icons">
    <p-select [(ngModel)]="selectedUom" [options]="uomOptions" optionLabel="label" optionValue="value" styleClass="w-auto" />
  </ng-template>
  <div class="col-xs">
    @if (isFinishedGoodsMode) {
      <p-card data-variant="compact">
        <p-table [value]="finishedGoodsBalances">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'Fabric_Summary_Table_Fabric' | translateWF: 'Fabric' }}</th>
              <th>{{ 'Fabric_Summary_Table_Type' | translateWF: 'Type' }}</th>
              <th>{{ 'Fabric_Summary_Table_TotalRolls' | translateWF: 'Total Rolls' }}</th>
              <th>{{ 'Fabric_Summary_Table_Loaded' | translateWF: 'Loaded' }}</th>
              <th>{{ 'Fabric_Summary_Table_Missing' | translateWF: 'Missing' }}</th>
              <th>{{ 'Fabric_Summary_Table_TotalWeight' | translateWF: 'Total Weight' }} ({{ uomSuffix }})</th>
              <th>{{ 'Fabric_Summary_Table_LoadedWeight' | translateWF: 'Loaded Weight' }} ({{ uomSuffix }})</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
            @let itemStrict = getFinishedGoodsBalanceStrict(item);
            <tr>
              <td>({{ itemStrict.fabricCode }}) {{ itemStrict.longDescription }}</td>
              <td>{{ itemStrict.type }}</td>
              <td>{{ itemStrict.totalRolls }}</td>
              <td>
                <span class="text-green-600 font-semibold">{{ itemStrict.loadedRolls }}</span>
              </td>
              <td>
                @if (itemStrict.missingRolls > 0) {
                  <span class="text-red-600 font-semibold">{{ itemStrict.missingRolls }}</span>
                } @else {
                  <span>{{ itemStrict.missingRolls }}</span>
                }
              </td>
              <td>{{ convertWeight(itemStrict.totalWeight, itemStrict.fabricCode) | number: '1.0-2' }}</td>
              <td>{{ convertWeight(itemStrict.loadedWeight, itemStrict.fabricCode) | number: '1.0-2' }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td>
                <strong>{{ 'Fabric_Summary_Table_Total' | translateWF: 'Total' }}</strong>
              </td>
              <td></td>
              <td>
                <strong>{{ getTotalFinishedGoodsRolls() }}</strong>
              </td>
              <td>
                <strong class="text-green-600">{{ getTotalLoadedRolls() }}</strong>
              </td>
              <td>
                @if (getTotalMissingRolls() > 0) {
                  <strong class="text-red-600">{{ getTotalMissingRolls() }}</strong>
                } @else {
                  <strong>{{ getTotalMissingRolls() }}</strong>
                }
              </td>
              <td>
                <strong>{{ getTotalFinishedGoodsWeightConverted() | number: '1.0-2' }}</strong>
              </td>
              <td>
                <strong>{{ getTotalLoadedWeightConverted() | number: '1.0-2' }}</strong>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    } @else {
      <!-- Column selector for optional columns (shown after batching) -->
      @if (showColumnSelector) {
        <p-card>
          <div class="form-box">
            <label class="text-sm font-medium text-gray-600">{{ 'Fabric_Summary_Table_AdditionalColumns' | translateWF: 'Additional Columns:' }}</label>
            <p-multiselect
              [options]="availableOptionalColumns"
              [formControl]="selectedColumnsControl"
              optionLabel="header"
              optionValue="field"
              [placeholder]="'Fabric_Summary_Table_SelectColumns' | translateWF: 'Select columns'"
              [maxSelectedLabels]="3"
              styleClass="w-auto"
            />
          </div>
        </p-card>
      }

      <!-- Info message explaining weight columns -->
      @if (hasBundleWeights) {
        <p-message icon="pi pi-info-circle" severity="info">
          <span>
            <strong>{{ 'Fabric_Summary_Table_NoteWeightDifference' | translateWF: 'Note:' }}</strong>
            <span>
              {{
                'Fabric_Summary_Table_NoteWeightDifferenceText'
                  | translateWF
                    : 'Roll Weight Balance is calculated from roll records and may differ from actual weight due to handling,
          moisture
          loss, or trimming. Bundle Weight Balance reflects the actual measured weight of weighed bundles. Roll count
          remains
          accurate.'
              }}
            </span>
          </span>
        </p-message>
      }
      <p-card data-variant="compact">
        <p-table [value]="productionCardPlannedBalances">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'Fabric_Summary_Table_Fabric' | translateWF: 'Fabric' }}</th>
              <th>{{ 'Fabric_Summary_Table_Type' | translateWF: 'Type' }}</th>
              @if (isColumnVisible('plannedBalance')) {
                <th>
                  {{ 'Fabric_Summary_Table_PlannedBalance' | translateWF: 'Planned Balance' }} ({{ uomSuffix }})
                  <i
                    class="pi pi-info-circle ml-1 text-gray-400 cursor-pointer"
                    [pTooltip]="'Fabric_Summary_Table_PlannedBalanceTooltip' | translateWF: 'Original planned quantity from production card'"
                    tooltipPosition="top"
                  ></i>
                </th>
              }
              <th>
                @if (isAtOrAfterBatching) {
                  {{ 'Fabric_Summary_Table_RollWeightBalance' | translateWF: 'Roll Weight Balance' }} ({{ uomSuffix }})
                  <i
                    class="pi pi-info-circle ml-1 text-gray-400 cursor-pointer"
                    [pTooltip]="'Fabric_Summary_Table_RollWeightBalanceTooltip' | translateWF: 'Roll-based quantity calculated from roll records. May differ from actual weight.'"
                    tooltipPosition="top"
                  ></i>
                } @else {
                  {{ 'Fabric_Summary_Table_CurrentBalance' | translateWF: 'Current Balance' }} ({{ uomSuffix }})
                }
              </th>
              @if (isColumnVisible('bundleWeightBalance')) {
                <th>
                  {{ 'Fabric_Summary_Table_BundleWeightBalance' | translateWF: 'Bundle Weight Balance' }} ({{ uomSuffix }})
                  <i
                    class="pi pi-info-circle ml-1 text-gray-400 cursor-pointer"
                    [pTooltip]="'Fabric_Summary_Table_BundleWeightBalanceTooltip' | translateWF: 'Actual measured weight from weighed bundles. This is the physical truth.'"
                    tooltipPosition="top"
                  ></i>
                </th>
              }
              <th>{{ 'Fabric_Summary_Table_RollCount' | translateWF: 'Roll Count' }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-item>
            @let itemStrict = getProductionCardBalanceStrict(item);
            <tr>
              <td>
                <div class="flex justify-between">
                  <div class="">({{ itemStrict.fabricCode }}) {{ itemStrict.longDescription }}</div>

                  @if (showWarningExceedsIcon(itemStrict)) {
                    <div class="" [pTooltip]="'Fabric_Summary_Table_ExceedsPlannedQuantity' | translateWF: 'Exceeds planned quantity'" tooltipPosition="top">
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 32 32">
                        <path fill="none" d="M16 26a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 16 26m-1.125-5h2.25v-9h-2.25Z" />
                        <path
                          fill="#eab308"
                          d="M16.002 6.171h-.004L4.648 27.997l.003.003h22.698l.002-.003ZM14.875 12h2.25v9h-2.25ZM16 26a1.5 1.5 0 1 1 1.5-1.5A1.5 1.5 0 0 1 16 26"
                        />
                        <path
                          fill="#eab308"
                          d="M29 30H3a1 1 0 0 1-.887-1.461l13-25a1 1 0 0 1 1.774 0l13 25A1 1 0 0 1 29 30M4.65 28h22.7l.001-.003L16.002 6.17h-.004L4.648 27.997Z"
                        />
                      </svg>
                    </div>
                  }
                </div>
              </td>
              <td>{{ itemStrict.type }}</td>
              @if (isColumnVisible('plannedBalance')) {
                <td>{{ convertWeight(itemStrict.planned, itemStrict.fabricCode) | number: '1.0-2' }}</td>
              }
              <td>{{ convertWeight(itemStrict.currentBalance, itemStrict.fabricCode) | number: '1.0-2' }}</td>
              @if (isColumnVisible('bundleWeightBalance')) {
                <!-- Per-fabric bundle weight is not shown because bundles weigh multiple rolls together -->
                <!-- We cannot accurately determine individual fabric weights after bundling -->
                <td>
                  <span class="text-gray-400">-</span>
                </td>
              }
              <td>{{ itemStrict.rollCount }}</td>
            </tr>
          </ng-template>
          <ng-template pTemplate="footer">
            <tr>
              <td>
                <strong>{{ 'Fabric_Summary_Table_Total' | translateWF: 'Total' }}</strong>
              </td>
              <td></td>
              @if (isColumnVisible('plannedBalance')) {
                <td>
                  <strong>{{ getTotalPlannedBalanceConverted() | number: '1.0-2' }}</strong>
                </td>
              }
              <td>
                <strong>{{ getTotalCurrentBalanceConverted() | number: '1.0-2' }}</strong>
              </td>
              @if (isColumnVisible('bundleWeightBalance')) {
                <td>
                  <!-- Cannot convert bundle total to yards without fabric-specific conversion factors -->
                  @if (selectedUom === 'yd') {
                    <strong class="text-gray-400">-</strong>
                  } @else {
                    <strong class="text-green-700">{{ getTotalBundleWeightBalanceConverted() | number: '1.0-2' }}</strong>
                  }
                </td>
              }
              <td>
                <strong>{{ getTotalRollCount() }}</strong>
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    }
  </div>
</p-panel>
